/**
 * room-web's sections, at runtime — the type is derived from it, so a section cannot exist without being on
 * this list, which is what `activity/screens.coverage.test.js` holds to a screen code (ADR 0012 step 29).
 */
export const ROOM_VIEWS = ['overview', 'tournaments', 'leaderboards', 'fantasy', 'ratings', 'players', 'settings'] as const;

export type RoomView = (typeof ROOM_VIEWS)[number];

/**
 * The room features a pane can depend on. Narrower than `RoomFeature` in the API client on
 * purpose: `ratings` gates a top-level section rather than a pane, so listing it here would offer
 * a value nothing under Settings can honour.
 */
export type RoomFeatureGate = 'leaderboard' | 'fantasy';

/**
 * Settings is a section, not a screen: everything the room configures *about itself* lives under
 * it, one pane each, rather than competing with the day's work for a slot in the top navigation.
 *
 * Templates is one of these now. It reads like an operational area but isn't one — it is the room
 * filling in what the platform defined, done once and revisited when the platform changes
 * something, which is the same shape as the two panes beside it.
 *
 * It stays first, and is the pane Settings opens on, because it is the only one that can be
 * *outstanding*: a template the room hasn't filled in can't be used by that room, while a tag
 * set, the feature switches and an operator's own preferences are always in a valid state.
 *
 * Points Systems sits directly after it, and the pair reads as one idea: both are things the room
 * sets up once and returns to when something changes, and neither is a day's work the way the
 * tournaments list or a leaderboard is. It is second rather than first because a points system,
 * unlike a template, is never *outstanding* — a room with none is a room that has not needed one.
 *
 * Salary Models sits after it and reads as the third of that group. It is the fantasy half of the
 * same idea — a points system says what a finish is worth, a salary model says what a player costs
 * to roster — and it is set up once and returned to for the same reasons. It is gated on
 * `fantasy`, since a fantasy contest is what consumes a salary model, exactly as Points Systems is
 * gated on `leaderboard`.
 *
 * Preferences stays last, and now holds **two** things rather than one: the room's default tags,
 * which used to be a pane of their own, and the operator's own settings. That pane is therefore no
 * longer purely "not about the room", so the distinction it used to make in its position is made
 * inside it instead — two cards, each stating whose it is, with the shared one first. Anything
 * added here has to say which of the two it is; that is the whole of what keeps the pane readable
 * now that it carries both.
 */
export type SettingsPane = 'templates' | 'points' | 'salary' | 'room' | 'preferences';

export const SETTINGS_PANES: readonly {
  pane: SettingsPane;
  label: string;
  /**
   * The room feature this pane depends on, or absent for the panes every room has. The shell
   * greys out a section the platform hasn't enabled rather than hiding it, and the sub-navigation
   * says the same thing the same way — a pane that vanished would read as a missing feature of
   * the *app*, which is the one thing it isn't.
   */
  requires?: RoomFeatureGate;
}[] = [
  { pane: 'templates', label: 'Templates' },
  // Gated on leaderboards because a leaderboard is what consumes a points system.
  { pane: 'points', label: 'Points Systems', requires: 'leaderboard' },
  // Gated on fantasy because a fantasy contest is what consumes a salary model.
  { pane: 'salary', label: 'Salary Models', requires: 'fantasy' },
  { pane: 'room', label: 'Room' },
  // Default tags live in here too — see the note above.
  { pane: 'preferences', label: 'Preferences' },
];

/** Who is asking: the two staff roles room-web admits (App refuses anybody holding neither). */
export interface StaffUser {
  isAdmin: boolean;
  isRoomOperator: boolean;
}

/** The room features the panes are gated on. */
export interface PaneRoom {
  canLeaderboard: boolean;
  canFantasy: boolean;
}

/**
 * The Settings panes a user is offered at a room, in order, each with whether the room has its feature on
 * (docs/specs/user-preferences.md UP25, A6). **The role does not matter, and that is the rule rather than an
 * omission**: an administrator and a room operator see all of Settings, Preferences and its Periods card included —
 * the operator's own choices are theirs to make wherever the room's settings are not. What restricts the room's
 * settings is elsewhere and unchanged: the Room pane is read-only to everyone, and the server refuses an operator's
 * room write (known gap 36).
 */
export function settingsPanesFor(
  user: StaffUser,
  room: PaneRoom,
): { pane: SettingsPane; label: string; available: boolean }[] {
  void user;
  return SETTINGS_PANES.map(({ pane, label, requires }) => ({
    pane,
    label,
    available: requires === 'leaderboard' ? room.canLeaderboard : requires === 'fantasy' ? room.canFantasy : true,
  }));
}

/**
 * The cards of the Preferences pane, in order: the room's default tags (shared), then the operator's own — the room to
 * open on, saved in this browser — then Periods, held for the session (UP25, A6).
 */
export type PreferencesCard = 'defaultTags' | 'yours' | 'periods';

export const PREFERENCES_CARDS: readonly PreferencesCard[] = ['defaultTags', 'yours', 'periods'];

/** A field of the Periods card: the Rundown period's select, or the Overview totals' select. */
export type PeriodField = 'rundown' | 'totals';

/**
 * Where a way into the app's sections leads: a section; for Settings the pane to open on; and, for a Change link
 * beside a period, the card to bring into view and the field to focus (A6b). The navigation's own entries carry a
 * section alone, so Settings opened from the navigation is unchanged: its first pane, at the top.
 */
export interface Destination {
  view: RoomView;
  pane?: SettingsPane;
  card?: PreferencesCard;
  field?: PeriodField;
}

/** The navigation's own entry for a section: the section and nothing else. */
export function sectionDestination(view: RoomView): Destination {
  return { view };
}

/**
 * A Change link beside a period (UP21, UP24, A6b): Settings → Preferences with the Periods card in view and focus on
 * the field that sets the period beside the link — the rail's (bar, tablet strip, phone line) the Rundown period,
 * Overview's the Overview totals.
 */
/** A Change link's request to the Periods card: the field to focus, new on every click so a repeat still reveals. */
export interface PeriodFocus {
  field: PeriodField;
  seq: number;
}

/** The player import's link to where a room adapts its players template (PI78). */
export function templatesDestination(): Destination {
  return { view: 'settings', pane: 'templates' };
}

export function changeDestination(from: 'rail' | 'overview'): Destination {
  return { view: 'settings', pane: 'preferences', card: 'periods', field: from === 'rail' ? 'rundown' : 'totals' };
}

/**
 * The pane Settings shows after going to `to` from `at`: the pane `to` names; otherwise the first pane when arriving
 * from another section, as Settings has always opened; otherwise the pane already on screen.
 */
export function settingsPaneAfter(at: { view: RoomView; pane: SettingsPane }, to: Destination): SettingsPane {
  if (to.pane) return to.pane;
  return to.view === 'settings' && at.view !== 'settings' ? SETTINGS_PANES[0]!.pane : at.pane;
}
